<template>
  <BaseWidget
    :title="widgetTitle"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :show-info-icon="true"
    info-tooltip="Total outstanding amount from all due and overdue invoices, across all time"
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/4753023-quick-guide-to-invoices', text: 'Learn about Invoices' }"
    :overflow-menu-items="taxMenuItems"
    @remove-widget="$emit('remove-widget', props.widgetId)"
    @overflow-select="handleOverflowSelect"
  >
    <div
      v-if="loading"
      class="widget-content-wrapper"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue
        :value="unpaidInvoicesFormatted"
        :subtitle="subtitleText"
      >
        <template #subtitle-suffix>
          <span
            class="focused-view-all-link"
            @click="handleViewAllClick"
          >View all</span>
        </template>
      </MetricValue>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useUnpaidInvoicesData } from '../composables/useWidgetData.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const excludeTax = ref(props.props?.excludeTax || false)

const widgetTitle = 'Unpaid invoices'

const taxMenuItems = computed(() => [
  { label: 'Exclude tax', value: 'exclude-tax', active: excludeTax.value },
  { label: 'Include tax', value: 'include-tax', active: !excludeTax.value }
])

// Keep the excl-tax state in the subtitle so the title can't wrap and hide labels (SDWEB-18441).
const subtitleText = computed(() => (excludeTax.value ? 'All time · Excl. tax' : 'All time'))

const { data, loading, error, cleanup, refresh } = useUnpaidInvoicesData({
  variables: computed(() => ({ excludeTax: excludeTax.value })),
  priority: 1
})

const unpaidInvoicesAmount = computed(() => {
  return data.value?.unpaidInvoicesTotal || 0
})

const formatCurrency = (amount) => {
  const currencySymbol = data.value?.currency_symbol || '$'
  const locale = (data.value?.locale || 'en-US').replace(/_/g, '-')

  if (amount === 0) return `${currencySymbol}0`

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: data.value?.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (e) {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const unpaidInvoicesFormatted = computed(() => {
  return formatCurrency(unpaidInvoicesAmount.value)
})

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  }
}

// Handle "View all" click - navigate to /invoices with DUE,OVERDUE status filter
const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const navigationUrl = `/invoices?status=DUE,OVERDUE`

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (e) {
    console.error('Error posting ReplaceUrl message:', e)
  }
}

const handleOverflowSelect = (value) => {
  const newExcludeTax = value === 'exclude-tax'
  if (newExcludeTax !== excludeTax.value) {
    excludeTax.value = newExcludeTax
    emit('update:props', { excludeTax: newExcludeTax })
  }
}

watch(() => props.props?.excludeTax, (newVal) => {
  if (newVal !== undefined && newVal !== excludeTax.value) {
    excludeTax.value = newVal
  }
})

watch(excludeTax, () => {
  refresh(true, { showLoading: true })
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  align-self: flex-end;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}
</style>
